'use client';

import React, { useState, createContext, useContext, useEffect, type ReactNode } from 'react';
import {
  IdTokenResult,
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { filterStandardClaims } from "next-firebase-auth-edge/lib/auth/claims";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, onSnapshot, updateDoc, orderBy, arrayUnion, writeBatch } from 'firebase/firestore';
import { auth, db } from '@/doi-tac-module/lib/firebase/config';
import {
  Role,
  PartnershipStatus,
  AgencyProfile,
  UserProfile,
  AgencyNotification
} from '@/doi-tac-module/lib/types';
import { login, logout as apiLogout } from '../../../api';

function toAuthTime(date: string) {
  return new Date(date).getTime() / 1000;
}

export const toUser = (user: FirebaseUser, idTokenResult: IdTokenResult): UserProfile => {
  return {
    uid: user.uid,
    email: user.email,
    fullName: user.displayName || 'Người dùng',
    role: 'guest', // Default, will be updated by Firestore
    roles: [],
    hasAspiration: false,
    isProfileComplete: false,
    referredByCode: null,
    urlAvatar: user.photoURL || '',
    partnershipStatus: 'UNREGISTERED',
  };
};

export type PostLoginAction = {
  type: 'APPLY_JOB';
  data: {
    jobId: string;
    jobTitle: string;
  };
} | null;

interface AuthContextType {
  user: UserProfile | null;
  role: Role;
  isLoggedIn: boolean;
  isLoading: boolean;
  setRole: (role: Role) => void;
  logout: () => Promise<void>;
  postLoginAction: PostLoginAction;
  setPostLoginAction: (action: PostLoginAction) => void;
  clearPostLoginAction: () => void;
  mockStatus: PartnershipStatus | null;
  setMockStatus: (status: PartnershipStatus | null) => void;
  mockRole: Role | null;
  setMockRole: (role: Role | null) => void;
  notifications: AgencyNotification[];
  addNotification: (notif: AgencyNotification) => void;
  updateNotificationStatus: (id: string, status: 'APPROVED' | 'REJECTED', reason?: string) => void;
  markNotificationsAsRead: () => void;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  updateCandidateAgency: (agencyName: string) => Promise<boolean>;
  isAgent: boolean;
  isMasterAgent: boolean;
  isApprovedAgent: boolean;
  isEmployer: boolean;
  updateUserLocally: (updates: Partial<UserProfile>) => void;
  realUser: UserProfile | null;
  impersonatedUid: string | null;
  impersonateUser: (uid: string) => void;
  stopImpersonating: () => void;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    return {
      user: null,
      isLoggedIn: false,
      isLoading: false, // Dừng load để không bị kẹt spinner
      isApprovedAgent: false,
      logout: async () => { },
      realUser: null,
      impersonatedUid: null,
      impersonateUser: () => { },
      stopImpersonating: () => { },
      isImpersonating: false,
    } as any;
  }
  return context;
};

export const AuthProvider = ({
  children,
  serverUser = null
}: {
  children: ReactNode,
  serverUser?: any
}) => {
  const [realUser, setRealUser] = useState<UserProfile | null>(serverUser);
  const [impersonatedUser, setImpersonatedUser] = useState<UserProfile | null>(null);
  const [impersonatedUid, setInternalImpersonatedUid] = useState<string | null>(null);

  const [role, setInternalRole] = useState<Role>(serverUser?.role || 'guest');
  const [isLoading, setIsLoading] = useState(!serverUser);
  const [notifications, setNotifications] = useState<AgencyNotification[]>([]);
  const [hiddenNotifications, setHiddenNotifications] = useState<string[]>([]);
  const [postLoginAction, setPostLoginAction] = useState<PostLoginAction>(null);
  const [mockStatus, setInternalMockStatus] = useState<PartnershipStatus | null>(null);
  const [mockRole, setInternalMockRole] = useState<Role | null>(null);

  const user = impersonatedUid && impersonatedUser ? impersonatedUser : realUser;

  // Load impersonatedUid from sessionStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const uid = sessionStorage.getItem('impersonatedUid');
      if (uid) setInternalImpersonatedUid(uid);
    }
  }, []);

  const impersonateUser = (uid: string) => {
    setInternalImpersonatedUid(uid);
    sessionStorage.setItem('impersonatedUid', uid);
  };

  const stopImpersonating = () => {
    setInternalImpersonatedUid(null);
    setImpersonatedUser(null);
    sessionStorage.removeItem('impersonatedUid');
    if (realUser) setInternalRole(realUser.role || 'guest');
  };

  // Load notifications from localStorage on mount
  useEffect(() => {
    /* Temporarily disabled localStorage for notifications
    if (typeof window !== 'undefined') {
      const savedNotifs = localStorage.getItem('partner_notifications');
      if (savedNotifs) {
        try {
            setNotifications(JSON.parse(savedNotifs));
        } catch (e) {
            console.error("Failed to parse notifications", e);
        }
      }
    }
    */
  }, []);

  const saveNotifications = (newNotifs: AgencyNotification[]) => {
    setNotifications(newNotifs);
    // Temporarily disabled localStorage for notifications
    // localStorage.setItem('partner_notifications', JSON.stringify(newNotifs));
  };

  // Load mock status from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedMock = localStorage.getItem('partner_mock_status');
      if (savedMock) {
        setInternalMockStatus(savedMock as PartnershipStatus);
      }
    }
  }, []);

  const setMockStatus = (status: PartnershipStatus | null) => {
    setInternalMockStatus(status);
    if (status) {
      localStorage.setItem('partner_mock_status', status);
    } else {
      localStorage.removeItem('partner_mock_status');
    }
  };

  const isLoggedIn = role !== 'guest';

  const setRole = (newRole: Role) => {
    setInternalRole(newRole);
  };

  const setMockRole = (role: Role | null) => {
    setInternalMockRole(role);
    if (role) {
      localStorage.setItem('partner_mock_role', role);
    } else {
      localStorage.removeItem('partner_mock_role');
    }
  };

  // Load mock role from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedRole = localStorage.getItem('partner_mock_role');
      if (savedRole) {
        setInternalMockRole(savedRole as Role);
      }
    }
  }, []);

  const clearPostLoginAction = () => {
    setPostLoginAction(null);
  };

  const logout = async () => {
    await signOut(auth);
    await apiLogout();
    setRealUser(null);
    setImpersonatedUser(null);
    setInternalImpersonatedUid(null);
    sessionStorage.removeItem('impersonatedUid');
    setInternalRole('guest');
    window.location.href = '/';
  };

  useEffect(() => {
    let unsubscribeFirestore: (() => void) | null = null;

    const unsubscribeAuth = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setIsLoading(true);
        try {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const issuedAtTime = toAuthTime(idTokenResult.issuedAtTime);

          // Sync cookie with server
          await login(idTokenResult.token);

          // If we don't have a server user or the session is newer, reload to sync server state
          if (!serverUser || (serverUser?.authTime && serverUser?.authTime < issuedAtTime)) {
            // Check for redirect parameter
            const params = new URLSearchParams(window.location.search);
            const redirectPath = params.get('redirect');
            if (redirectPath) {
              window.location.href = redirectPath;
            } else {
              window.location.reload();
            }
            return;
          }

          const userDocRef = doc(db, 'users', firebaseUser.uid);
          unsubscribeFirestore = onSnapshot(userDocRef, async (userDoc) => {
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const userRole = (userData.role as Role) || 'company';
              const isAgentUser = userRole === 'CTV' || userRole === 'MASTER_CTV' || userRole === 'ADMIN';
              const fullName = (isAgentUser && userData.agencyProfile?.managingDirector)
                ? userData.agencyProfile.managingDirector
                : (userData.fullName || firebaseUser.displayName || 'Người dùng');

              const profile = {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                fullName: fullName,
                role: userRole,
                roles: userData.roles || [userRole],
                hasAspiration: userData.hasAspiration || false,
                isProfileComplete: userData.isProfileComplete || false,
                referredByCode: userData.referredByCode || null,
                urlAvatar: userData.urlAvatar || firebaseUser.photoURL || '',
                partnershipStatus: (userData.partnershipStatus as PartnershipStatus) || 'UNREGISTERED',
                agencyProfile: userData.agencyProfile ? { ...userData.agencyProfile, role: userRole } as AgencyProfile : undefined,
              };

              setRealUser(profile);
              if (!sessionStorage.getItem('impersonatedUid')) {
                setInternalRole(userRole);
              }
            }
            setIsLoading(false);
          }, (error) => {
            console.error("Firestore Profile Sync Error:", error);
            setIsLoading(false);
          });
        } catch (error) {
          console.error("Auth initialization error:", error);
          setIsLoading(false);
        }
      } else {
        if (!serverUser) {
          setRealUser(null);
          setImpersonatedUser(null);
          setInternalRole('guest');
        }
        setIsLoading(false);
      }
    });

    // Redundant cookie sync removed as RootProvider now handles this centrally with reload() support

    // Listener for impersonated user
    let unsubscribeImpersonated: (() => void) | null = null;
    if (impersonatedUid) {
      unsubscribeImpersonated = onSnapshot(doc(db, 'users', impersonatedUid), (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const userRole = (userData.role as Role) || 'company';
          const isAgentUser = userRole === 'CTV' || userRole === 'MASTER_CTV' || userRole === 'ADMIN';
          const fullName = (isAgentUser && userData.agencyProfile?.managingDirector)
            ? userData.agencyProfile.managingDirector
            : (userData.fullName || 'Người dùng');

          const profile: UserProfile = {
            uid: docSnap.id,
            email: userData.email || null,
            fullName: fullName,
            role: userRole,
            roles: userData.roles || [userRole],
            hasAspiration: userData.hasAspiration || false,
            isProfileComplete: userData.isProfileComplete || false,
            referredByCode: userData.referredByCode || null,
            urlAvatar: userData.urlAvatar || '',
            partnershipStatus: (userData.partnershipStatus as PartnershipStatus) || 'UNREGISTERED',
            agencyProfile: userData.agencyProfile ? { ...userData.agencyProfile, role: userRole } as AgencyProfile : undefined,
          };
          setImpersonatedUser(profile);
          setInternalRole(userRole);
        }
      });
    }

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) unsubscribeFirestore();
      if (unsubscribeImpersonated) unsubscribeImpersonated();
    };
  }, [impersonatedUid, serverUser]);

  // Inherit the onboarding preferences logic from the original AuthContext
  useEffect(() => {
    const preferencesRaw = sessionStorage.getItem('onboardingPreferences');
    const isNewCandidate = role === 'company' && user?.hasAspiration === false;

    if (isNewCandidate && preferencesRaw) {
      try {
        const preferences = JSON.parse(preferencesRaw);
        const existingProfileRaw = localStorage.getItem('generatedCandidateProfile');
        let profile = existingProfileRaw ? JSON.parse(existingProfileRaw) : {};

        profile = {
          ...profile,
          desiredIndustry: preferences.desiredIndustry || profile.desiredIndustry,
          aspirations: {
            ...profile.aspirations,
            desiredVisaType: preferences.desiredVisaType,
            desiredVisaDetail: preferences.desiredVisaDetail,
            desiredLocation: preferences.desiredLocation,
          }
        };
        localStorage.setItem('generatedCandidateProfile', JSON.stringify(profile));
        sessionStorage.removeItem('onboardingPreferences');
        setRole('company'); // This stays candidate but aspirations are updated
      } catch (e) {
        console.error("Failed to apply onboarding preferences:", e);
        sessionStorage.removeItem('onboardingPreferences');
      }
    } else if (isNewCandidate && !preferencesRaw) {
      localStorage.removeItem('generatedCandidateProfile');
    }
  }, [role, user?.hasAspiration]);

  // Real-time notifications for Sales Manager: Listen to BOTH persistent logs AND active PENDING users
  useEffect(() => {
    const currentRole = mockRole || role;
    const currentStatus = mockStatus || user?.partnershipStatus;
    const isAdmin = currentRole === 'ADMIN';
    const isMasterAgentValue = isAdmin || (currentRole === 'MASTER_CTV' && currentStatus === 'APPROVED');
    const isApprovedAgentValue = isMasterAgentValue || (currentRole === 'CTV' && currentStatus === 'APPROVED');

    if (isApprovedAgentValue) {
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

      let dbNotifs: AgencyNotification[] = [];
      let pendingUserNotifs: AgencyNotification[] = [];

      const updateMergedNotifications = () => {
        const mergedMap = new Map<string, AgencyNotification>();

        pendingUserNotifs.forEach(n => {
          if (n.senderUid) mergedMap.set(n.senderUid, n);
        });

        dbNotifs.forEach(n => {
          if (n.senderUid) {
            mergedMap.set(n.senderUid, n);
          } else {
            mergedMap.set(n.id, n);
          }
        });

        const merged = Array.from(mergedMap.values())
          .filter(n => {
            if (hiddenNotifications.includes(n.id)) return false;
            if (n.deletedBy?.includes(user?.uid || '')) return false;
            return true;
          })
          .sort((a, b) => b.timestamp - a.timestamp);

        setNotifications(merged);
      };

      const qLogs = isMasterAgentValue
        ? query(
          collection(db, "notifications"),
          where("timestamp", ">=", thirtyDaysAgo),
          orderBy("timestamp", "desc")
        )
        : query(
          collection(db, "notifications"),
          where("recipientUid", "==", user?.uid)
        );

      const unsubscribeLogs = onSnapshot(qLogs, (snapshot) => {
        let docs = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
        } as AgencyNotification));

        if (!isMasterAgentValue) {
          docs = docs
            .filter(doc => doc.timestamp >= thirtyDaysAgo)
            .sort((a, b) => b.timestamp - a.timestamp);
        }

        dbNotifs = docs;
        updateMergedNotifications();
      }, (error) => {
        console.error("Firestore Logan Error (Logs):", error);
      });

      let unsubscribeUsers = () => { };
      if (isMasterAgentValue) {
        const qUsers = query(
          collection(db, "users"),
          where("partnershipStatus", "==", "PENDING"),
          where("role", "==", "CTV")
        );

        unsubscribeUsers = onSnapshot(qUsers, (snapshot) => {
          pendingUserNotifs = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: 'REGISTRATION_REQUEST' as const,
              title: { vi: 'Yêu cầu đăng ký mới', bn: 'নতুন রেজিস্ট্রেশন অনুরোধ' },
              description: {
                vi: `Đại lý ${data.agencyProfile?.managingDirector || data.fullName || 'Mới'} gửi hồ sơ`,
                bn: `এজেন্সি ${data.agencyProfile?.managingDirector || data.fullName || 'নতুন'} আবেদন করেছে`
              },
              status: 'PENDING' as const,
              timestamp: data.registrationSubmittedAt || Date.now(),
              senderName: data.agencyProfile?.managingDirector || data.fullName || data.agencyProfile?.companyName || 'Unknown',
              senderCompanyName: data.agencyProfile?.companyName || undefined,
              senderUid: doc.id,
              read: false,
            } as AgencyNotification;
          });
          updateMergedNotifications();
        }, (error) => {
          console.error("Firestore Notification Error (Users):", error);
        });
      }

      return () => {
        unsubscribeLogs();
        unsubscribeUsers();
      };
    } else {
      setNotifications([]);
    }
  }, [role, mockRole, mockStatus, user?.partnershipStatus, user?.uid, hiddenNotifications]);

  const value = {
    role: mockRole || role,
    isLoggedIn,
    isLoading,
    setRole,
    logout,
    postLoginAction,
    setPostLoginAction,
    clearPostLoginAction,
    mockStatus,
    setMockStatus,
    mockRole,
    setMockRole,
    notifications,
    addNotification: (notif: AgencyNotification) => {
      saveNotifications([notif, ...notifications]);
    },
    updateNotificationStatus: async (id: string, status: 'APPROVED' | 'REJECTED', reason?: string) => {
      const targetNotif = notifications.find(n => n.id === id);

      // Real-time Firestore logic for Sales Manager approving/rejecting Agency
      try {
        // 1. Update the Notification Log
        // If the ID is a user UID (from the legacy query), we might need to find if a log exists or create one
        let notifId = id;
        const existsInLogs = notifications.some(n => n.id === id && n.senderUid === id); // This is ambiguous, let's fix

        // Better check: is id a known log doc id? 
        // If we merged and id is from users collection, then id == senderUid.
        // If it's from log collection, id is a random string.

        const isLegacyId = targetNotif?.id === targetNotif?.senderUid;

        if (isLegacyId) {
          // It's a legacy entry from 'users' collection. 
          // We should create a new log entry instead of updating a non-existent doc in 'notifications'
          const newNotifRef = doc(collection(db, 'notifications'));
          await setDoc(newNotifRef, {
            ...targetNotif,
            id: newNotifRef.id,
            status,
            rejectionReason: reason || null,
            processedAt: Date.now(),
            read: true,
            timestamp: targetNotif?.timestamp || Date.now()
          });
          notifId = newNotifRef.id;
        } else {
          const notifRef = doc(db, 'notifications', id);
          await updateDoc(notifRef, {
            status,
            rejectionReason: reason || null,
            processedAt: Date.now(),
            read: true
          });
        }

        // 2. Update the Target User Profile (Old logic)
        if (targetNotif?.type === 'REGISTRATION_REQUEST' && targetNotif.senderUid) {
          const userRef = doc(db, 'users', targetNotif.senderUid);
          if (status === 'APPROVED') {
            const userSnap = await getDoc(userRef);
            const userData = userSnap.data();
            const currentProfile = userData?.agencyProfile || {};

            if (!currentProfile.code) {
              const { generateAgencyCode } = await import('@/doi-tac-module/lib/agency-utils');
              currentProfile.code = generateAgencyCode();
            }

            await updateDoc(userRef, {
              partnershipStatus: status,
              agencyProfile: currentProfile,
              updatedAt: Date.now()
            });
          } else {
            await updateDoc(userRef, {
              partnershipStatus: status,
              rejectionReason: reason || null,
              updatedAt: Date.now()
            });
          }

          if (targetNotif.senderUid === user?.uid) {
            setMockStatus(status);
          }
        }
      } catch (error) {
        console.error("Failed to update status in Firestore:", error);
      }

      const updated = notifications.map(n => n.id === id ? { ...n, status, rejectionReason: reason, read: true } : n);
      saveNotifications(updated);
    },
    markNotificationsAsRead: async () => {
      saveNotifications(notifications.map(n => ({ ...n, read: true })));
    },
    deleteNotification: async (id: string) => {
      const targetNotif = notifications.find(n => n.id === id);
      if (!targetNotif) return;

      // 1. Update local UI immediately
      setHiddenNotifications(prev => [...prev, id]);
      setNotifications(prev => prev.filter(n => n.id !== id));

      // 2. Persist to Firestore if it's a log entry
      const isLegacyId = targetNotif.id === targetNotif.senderUid;
      if (!isLegacyId && user?.uid) {
        try {
          const notifRef = doc(db, 'notifications', id);
          await updateDoc(notifRef, {
            deletedBy: arrayUnion(user.uid)
          });
        } catch (error) {
          console.error("Failed to delete notification in Firestore:", error);
        }
      }
    },
    deleteAllNotifications: async () => {
      if (notifications.length === 0) return;

      const idsToDelete = notifications.map(n => n.id);

      // 1. Update local UI immediately
      setHiddenNotifications(prev => [...prev, ...idsToDelete]);
      setNotifications([]);

      // 2. Persist to Firestore (Batch)
      if (user?.uid) {
        try {
          const batch = writeBatch(db);
          let count = 0;

          notifications.forEach(n => {
            const isLegacyId = n.id === n.senderUid;
            if (!isLegacyId) {
              const notifRef = doc(db, 'notifications', n.id);
              batch.update(notifRef, {
                deletedBy: arrayUnion(user!.uid)
              });
              count++;
            }
          });

          if (count > 0) {
            await batch.commit();
          }
        } catch (error) {
          console.error("Failed to delete all notifications in Firestore:", error);
        }
      }
    },
    updateCandidateAgency: async (agencyName: string) => {
      if (!user || user.role !== 'company') return false;

      // Simulating persistent storage update
      const existingProfileRaw = localStorage.getItem('generatedCandidateProfile');
      let profile = existingProfileRaw ? JSON.parse(existingProfileRaw) : {};
      profile.bangladeshAgency = agencyName;
      localStorage.setItem('generatedCandidateProfile', JSON.stringify(profile));

      // Update local user state
      if (impersonatedUid) {
        setImpersonatedUser(prev => prev ? { ...prev, role: 'company' } : null);
      } else {
        setRealUser(prev => prev ? { ...prev, role: 'company' } : null);
      }
      setInternalRole('company');

      return true;
    },
    updateUserLocally: (updates: Partial<UserProfile>) => {
      const updater = (prev: UserProfile | null) => {
        if (!prev) return null;

        // Merge agencyProfile specifically to avoid overwriting the whole object
        const updatedAgencyProfile = updates.agencyProfile
          ? { ...(prev.agencyProfile || {}), ...updates.agencyProfile } as AgencyProfile
          : prev.agencyProfile;

        return {
          ...prev,
          ...updates,
          agencyProfile: updatedAgencyProfile
        };
      };

      if (impersonatedUid) {
        setImpersonatedUser(updater);
      } else {
        setRealUser(updater);
      }
    }
  };

  // Enhance user object with mock status if available
  const currentRole = mockRole || (user ? user.role : 'guest');
  const currentRoles = user?.roles || (user?.role ? [user.role] : []);
  const currentStatus = mockStatus || (user ? user.partnershipStatus : undefined);

  const isAdmin = currentRoles.includes('ADMIN');
  const isAgent = currentRoles.includes('CTV') || currentRoles.includes('MASTER_CTV') || isAdmin;
  const isMasterAgent = isAdmin || (currentRoles.includes('MASTER_CTV') && currentStatus === 'APPROVED');
  const isApprovedAgent = isAdmin || (isAgent && currentStatus === 'APPROVED');
  const isEmployer = isAdmin || currentRoles.includes('company');

  const enhancedUser = user ? {
    ...user,
    role: currentRole,
    partnershipStatus: mockStatus || user.partnershipStatus,
    agencyProfile: isAgent ? {
      ...user.agencyProfile,
      code: user.agencyProfile?.code || (isAgent && currentStatus === 'APPROVED' ? '' : undefined)
    } : user.agencyProfile
  } : null;

  const contextValue: AuthContextType = {
    ...value,
    user: enhancedUser,
    isAgent,
    isMasterAgent,
    isApprovedAgent,
    isEmployer,
    realUser,
    impersonatedUid,
    impersonateUser,
    stopImpersonating,
    isImpersonating: !!impersonatedUid,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
